import clsx from 'clsx';
import { useMemo } from 'react';
import { PerformerCategoryIcon } from 'src/components/common/PerformerCategoryIcon';
import {
  MetricsDisplay,
  MetricsDisplayPlaceholder,
} from 'src/components/Metrics';
import { useAppContext } from 'src/contexts/AppContext';
import { useSidePanelContext } from 'src/contexts/SidePanelContext';
import { vars } from 'src/core/themes';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { getDescription } from 'src/utils/eventWithDataUtils';
import {
  getInventoryListingMetrics,
  getSalesSaleMetrics,
} from 'src/utils/ticketMetricUtils';
import {
  CatalogEntity,
  Feature,
  ListingMetrics,
  Performer,
  SaleMetrics,
} from 'src/WebApiController';

import * as styles from './PerformerVenueDisplay.css';

const GROUPING_METRICS_PLACEHOLDER_DIMS = { width: '50px' };

export function PerformerVenueDisplay({
  grouping,
  isSelected,
  onPerformerVenueClicked,
  metricsData,
  hideMetrics,
  disabled,
}: {
  grouping: CatalogEntity;
  isSelected: boolean;
  onPerformerVenueClicked: (group: CatalogEntity) => void;
  metricsData: Record<string, object> | undefined;
  hideMetrics?: boolean;
  disabled?: boolean;
}) {
  const { isCollapsed } = useSidePanelContext();
  const { mainRoute } = useAppContext();
  const { loginContext } = useAppContext();
  const hasConsignorMasterUI = useUserHasConsignorMasterUI();
  const hasUnfulfilledPercentFeature = useUserHasFeature(
    Feature.InventoryUnfulfilledPercentColumn
  );
  const hasPendingSourcingFeature = useUserHasFeature(
    Feature.InventoryPendingSourcingColumn
  );
  const displayMetrics = useMemo(() => {
    if (!metricsData) {
      return null;
    }
    return mainRoute === MainRoute.Sales
      ? getSalesSaleMetrics(
          metricsData[grouping.viagId!] as SaleMetrics,
          false,
          hasUnfulfilledPercentFeature,
          hasPendingSourcingFeature
        )
      : getInventoryListingMetrics(
          metricsData[grouping.viagId!] as ListingMetrics,
          loginContext?.user?.activeAccount?.currencyCode
        );
  }, [
    grouping.viagId,
    loginContext?.user?.activeAccount?.currencyCode,
    mainRoute,
    metricsData,
    hasUnfulfilledPercentFeature,
    hasPendingSourcingFeature,
  ]);

  const hasCategory = useMemo(() => {
    return (grouping as Performer).categ;
  }, [grouping]);

  const mainMetrics = useMemo(
    () => (mainRoute === MainRoute.Sales ? null : displayMetrics?.mainMetrics),
    [displayMetrics?.mainMetrics, mainRoute]
  );

  return (
    <div className={styles.performerVenueDisplayContainer}>
      <div
        key={grouping.viagId}
        onClick={() => {
          if (!disabled) {
            onPerformerVenueClicked(grouping);
          }
        }}
        title={`${grouping.name} - ${getDescription(grouping)}`}
        className={clsx(styles.mainGroupingDisplayDiv, {
          [styles.mainGroupingDisplaySelected]: isSelected,
          [styles.mainGroupingDisplayCollapsed]: isCollapsed,
          [styles.mainGroupingDisplayDivActive]: !disabled,
        })}
      >
        <div className={styles.performerVenueDiv}>
          <div className={styles.performerVenueNameDiv}>
            <div className={styles.performerVenueNameInfoDiv}>
              <div
                className={styles.headerContainer}
                style={disabled ? { color: vars.color.textPrimary } : {}}
              >
                <div className={styles.nameDiv}>{grouping.name}</div>
                {hasConsignorMasterUI ? null : (
                  <div className={styles.headerTicketContainer}>
                    {mainMetrics}
                  </div>
                )}
              </div>

              <div className={styles.totalDescription}>
                <div
                  className={styles.description}
                  style={disabled ? { color: vars.color.textPrimary } : {}}
                >
                  {hasCategory && (
                    <span className={styles.categoryIconContainer}>
                      <PerformerCategoryIcon
                        performer={grouping as Performer}
                      />
                    </span>
                  )}
                  {getDescription(grouping)?.replace(/ Music$/, '')}
                </div>
                {!hideMetrics && (
                  <span className={styles.metrics}>
                    {hasConsignorMasterUI ? null : displayMetrics == null ? (
                      <MetricsDisplayPlaceholder
                        {...GROUPING_METRICS_PLACEHOLDER_DIMS}
                      />
                    ) : (
                      <MetricsDisplay
                        mainMetrics={null}
                        subMetrics={displayMetrics.subMetrics}
                      />
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
